import { ENDPOINTS, TASK_TYPES } from 'features/daily-plan/constants';
import { SECONDARY_API_CLIENT } from 'services/API';
import { Permissions, userHasPermission } from 'services/Authorization';
import {
  formatSalesResponse,
  SALES_COLUMNS,
  SalesTab
} from 'UI/components/organisms/inventoryProfiles';
import { formatAssociatedCompaniesResponse } from 'UI/components/organisms/inventoryProfiles/AssociatedCompaniesTab/AssociatedCompanies.utils';
import {
  associatedCompaniesColumns,
  VirtualProps as AssociatedCompaniesVirtualProps
} from 'UI/components/organisms/inventoryProfiles/AssociatedCompaniesTab/associatedCompaniesColumns';
import AssociatedCompaniesTab, {
  TAB_INCLUDES
} from 'UI/components/organisms/inventoryProfiles/AssociatedCompaniesTab/AssociatedCompaniesTab';
import EmployeesTab from 'UI/components/organisms/inventoryProfiles/EmployeesTab';
import {
  EmployeesColumns,
  EmployeesCustomRender,
  EmployeesVirtualProps
} from 'UI/components/organisms/inventoryProfiles/EmployeesTab/columns';
import FeeAgreementTab from 'UI/components/organisms/inventoryProfiles/FeeAgreementTab';
import {
  feeAgreementColumns,
  feeAgreementVirtualProps
} from 'UI/components/organisms/inventoryProfiles/FeeAgreementTab/columns';
import HiringAuthorityTab from 'UI/components/organisms/inventoryProfiles/HiringAuthorityTab';
import {
  hiringAuthorityColumns,
  HiringAuthorityVirtualProps
} from 'UI/components/organisms/inventoryProfiles/HiringAuthorityTab/columns';
import JobOrdersTab from 'UI/components/organisms/inventoryProfiles/JobOrdersTab';
import { VirtualProps as jobOrderVirtualProps } from 'UI/components/organisms/inventoryProfiles/JobOrdersTab/candidatesJOcolumns';
import { companyProfileJobOrderColumns } from 'UI/components/organisms/inventoryProfiles/JobOrdersTab/companiesJOcolumns';
import PlacementsTab from 'UI/components/organisms/inventoryProfiles/PlacementsTab';
import {
  companyProfilePlacementColumns,
  IncludesByMode
} from 'UI/components/organisms/inventoryProfiles/PlacementsTab/columns';
import {
  handleEmployeesResponse,
  handleHiringAuthorityResponse
} from 'UI/components/organisms/inventoryProfiles/responseChange';
import SendoutsTab from 'UI/components/organisms/inventoryProfiles/SendoutsTab';
import {
  COMPANY_PROFILE_SENDOUTS_COLUMNS,
  SENDOUTS_RENDERERS,
  SENDOUTS_VIRTUAL_PROPS
} from 'UI/components/organisms/inventoryProfiles/SendoutsTab/columns';
import { ProfileTablesPreferenceKeys } from 'UI/components/organisms/inventoryProfiles/tabKeys';
import {
  formatTasksResponse,
  TASK_COLUMNS,
  TASKS_EXTRA_RENDERERS,
  TasksTab
} from 'UI/components/organisms/inventoryProfiles/TasksTab';
import { EnvApiUrls } from 'UI/constants/defaults';
import { Endpoints } from 'UI/constants/endpoints';
import { ENTITY_TYPE_ID_MAP, EntityType, TabKeys } from 'UI/constants/entityTypes';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { hasFeatureFlag } from 'UI/utils';

export const companiesTabDefinition = [
  {
    id: TabKeys.JobOrders,
    label: 'Job Orders',
    endpoint: Endpoints.JobOrders,
    Component: JobOrdersTab,
    props: {
      tablePreferencesKey: ProfileTablesPreferenceKeys.CompanyJobOrders,
      DataTableProps: {
        includes:
          'specialty,position,address,whiteSheet,hiringAuthorities,status,vRecruiter,lastActivityLog,keywordTags',
        apiVersion: 2,
        virtualProps: jobOrderVirtualProps,
        orderByOptions: { column: 'created_at', direction: 'desc' }
      },
      initialColumns: companyProfileJobOrderColumns
    }
  },
  {
    id: TabKeys.Sendouts,
    label: 'Sendouts',
    endpoint: Endpoints.Sendouts,
    Component: SendoutsTab,
    props: {
      tablePreferencesKey: ProfileTablesPreferenceKeys.CompanySendouts,
      extraRenderers: SENDOUTS_RENDERERS,
      DataTableProps: {
        apiVersion: 1,
        includes: 'type,status,candidate,jobOrder,candidateAccountable,jobOrderAccountable',
        virtualProps: SENDOUTS_VIRTUAL_PROPS
      },
      initialColumns: COMPANY_PROFILE_SENDOUTS_COLUMNS
    }
  },
  {
    id: TabKeys.Placements,
    label: 'Placements',
    endpoint: Endpoints.Placements,
    Component: PlacementsTab,
    props: {
      tablePreferencesKey: ProfileTablesPreferenceKeys.CompanyPlacements,
      DataTableProps: {
        apiVersion: 1,
        includes: IncludesByMode.company
      },
      initialColumns: companyProfilePlacementColumns
    }
  },
  {
    id: TabKeys.FeeAgreements,
    label: 'Fee Agreements',
    endpoint: Endpoints.FeeAgreement,
    Component: FeeAgreementTab,
    props: {
      tablePreferencesKey: ProfileTablesPreferenceKeys.CompanyFeeAgreements,
      DataTableProps: {
        includes:
          'feeAgreementStatus,hiringAuthority,creator,paymentScheme,hiringAuthority,vRegional,vCreator,vCoach,vProductionDirector',
        apiVersion: 2,
        virtualProps: feeAgreementVirtualProps
      },
      initialColumns: feeAgreementColumns
    }
  },
  {
    id: TabKeys.HiringAuthorities,
    label: 'Hiring Authorities',
    endpoint: Endpoints.HiringAuthorities,
    Component: HiringAuthorityTab,
    props: {
      tablePreferencesKey: ProfileTablesPreferenceKeys.CompanyHiringAuthorities,
      DataTableProps: {
        apiVersion: 2,
        virtualProps: HiringAuthorityVirtualProps,
        includes: 'lastActivityLog,specialty,subspecialty,position,phones,status,subStatus',
        formatResponse: handleHiringAuthorityResponse
      },
      initialColumns: hiringAuthorityColumns
    }
  },
  {
    id: TabKeys.AssociatedCompanies,
    label: 'Associated Companies',
    Component: AssociatedCompaniesTab,
    endpoint: Endpoints.AssociatedCompanies,
    props: {
      tablePreferencesKey: ProfileTablesPreferenceKeys.CompanyAssocietedCompanies,
      DataTableProps: {
        apiVersion: 2,
        virtualProps: AssociatedCompaniesVirtualProps,
        includes: TAB_INCLUDES.company,
        formatResponse: formatAssociatedCompaniesResponse
      },
      initialColumns: associatedCompaniesColumns
    }
  },
  {
    id: TabKeys.Employees,
    label: 'Employees',
    endpoint: 'employees',
    Component: EmployeesTab,
    props: {
      tablePreferencesKey: ProfileTablesPreferenceKeys.CompanyEmployees,
      DataTableProps: {
        virtualProps: EmployeesVirtualProps,
        formatResponse: handleEmployeesResponse,
        includes: 'keywordTags'
      },
      initialColumns: EmployeesColumns,
      extraRenderers: EmployeesCustomRender
    }
  },
  {
    id: TabKeys.Tasks,
    label: 'Action Plan',
    Component: TasksTab,
    visible: () =>
      userHasPermission(Permissions.DailyPlan.CanUseModule) &&
      hasFeatureFlag(FeatureFlags.DailyPlanAddTaskFromProfile),
    props: {
      allowedTaskTypes: [TASK_TYPES.marketCandidate, TASK_TYPES.other],
      DataTableProps: {
        apiVersion: 1,
        endpoint: ENDPOINTS.tasks,
        formatResponse: formatTasksResponse
      },
      entityType: EntityType.Company,
      entityTypeId: ENTITY_TYPE_ID_MAP.company,
      extraRenderers: TASKS_EXTRA_RENDERERS,
      initialColumns: TASK_COLUMNS,
      tablePreferencesKey: ProfileTablesPreferenceKeys.CompanyTasks
    }
  },
  {
    id: TabKeys.Sales,
    label: 'Sales',
    Component: SalesTab,
    visible: hasFeatureFlag(FeatureFlags.CompaniesSalesTab),
    props: {
      tablePreferencesKey: ProfileTablesPreferenceKeys.CompanySales,
      DataTableProps: {
        endpoint: `${EnvApiUrls.API_URL_AWS}/${Endpoints.CompanySales}`,
        apiInstance: SECONDARY_API_CLIENT,
        formatResponse: formatSalesResponse
      },
      initialColumns: SALES_COLUMNS
    }
  }
];
